import React, { useEffect, useState } from "react";
import api, { inr } from "../../lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { ShoppingBag, IndianRupee, Users, Package, TrendingUp, Lock, Zap, ClipboardCheck } from "lucide-react";

const FULFILMENT_STEP_COUNT = 6; // mirrors FULFILMENT_STEPS in server.py

const STAT_CARDS = (s) => {
  const cards = [
    { label: "New Orders", value: s.unclaimed_orders, icon: Zap, hint: "Unclaimed — click to view", to: "/admin/orders?unclaimed=true", highlight: true },
    { label: "Total Orders", value: s.total_orders, icon: ShoppingBag, hint: "All-time" },
    { label: "Customers", value: s.total_customers, icon: Users, hint: "Registered accounts" },
    { label: "Products", value: s.total_products, icon: Package, hint: "Active catalog" },
  ];
  if (s.total_revenue !== undefined) {
    cards.splice(2, 0, { label: "Total revenue", value: inr(s.total_revenue), icon: IndianRupee, hint: `${s.total_orders} orders all-time` });
  }
  // Only present in the API response at all for accounts holding
  // financials.view_net_margin — the backend omits the field entirely for
  // everyone else, so there's nothing to gate on the frontend here.
  if (s.net_margin !== undefined) {
    cards.push({
      label: "Net margin", value: inr(s.net_margin), icon: IndianRupee,
      hint: "Admin only", locked: true,
    });
  }
  return cards;
};

export default function AdminDashboardPage() {
  const { user, can } = useAuth();
  const [stats, setStats] = useState(null);
  const [myOrders, setMyOrders] = useState(null);
  const canFulfil = can("orders.fulfil") || can("orders.edit_cancel_refund");

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);

  useEffect(() => {
    if (!canFulfil) return;
    api.get("/admin/orders?mine=true").then(({ data }) => {
      // "Active" = still my responsibility — hasn't left fulfilment yet.
      const active = data.filter((o) => !["shipped", "delivered", "cancelled"].includes(o.tracking_status));
      setMyOrders(active);
    });
  }, [canFulfil]);

  if (!stats) return <div className="text-welljio-muted">Loading dashboard...</div>;
  const greetingName = user?.role === "admin" ? "Admin" : user?.name;

  return (
    <div data-testid="admin-dashboard" className="space-y-10">
      <div>
        <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-2">Overview</p>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Welcome back, {greetingName}.</h1>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {STAT_CARDS(stats).map((c) => {
          const CardTag = c.to ? Link : "div";
          const cardProps = c.to ? { to: c.to, "data-testid": "dashboard-new-orders-card" } : {};
          return (
            <CardTag
              key={c.label}
              {...cardProps}
              className={`bg-white border rounded-3xl p-6 transition-colors ${
                c.locked
                  ? "border-welljio-coral/30"
                  : c.highlight
                  ? "border-welljio-coral/40 hover:bg-welljio-coralSoft/40 cursor-pointer"
                  : "border-slate-100"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs uppercase tracking-widest text-welljio-subtle">{c.label}</p>
                {c.locked ? (
                  <span className="flex items-center gap-1 text-[10px] font-medium text-welljio-coral bg-welljio-coralSoft px-2 py-0.5 rounded-full">
                    <Lock className="w-2.5 h-2.5" strokeWidth={2}/> Admin only
                  </span>
                ) : c.highlight && c.value > 0 ? (
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-welljio-coral bg-welljio-coralSoft px-2 py-0.5 rounded-full">
                    <Zap className="w-2.5 h-2.5" strokeWidth={2.2}/> Needs action
                  </span>
                ) : (
                  <c.icon className={`w-4 h-4 ${c.highlight ? "text-welljio-coral" : "text-welljio-accent"}`} strokeWidth={1.5}/>
                )}
              </div>
              <p className={`font-heading text-3xl font-bold ${c.highlight && c.value > 0 ? "text-welljio-coral" : ""}`}>{c.value}</p>
              {!c.locked && <p className="text-xs text-welljio-muted mt-1">{c.hint}</p>}
            </CardTag>
          );
        })}
      </div>

      {canFulfil && myOrders && myOrders.length > 0 && (
        <section data-testid="my-active-orders-section" className="bg-white border border-slate-100 rounded-3xl p-7">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" strokeWidth={1.5}/> My Active Orders
          </h2>
          <div className="space-y-1">
            {myOrders.map((o) => {
              const done = (o.fulfilment_steps || []).length;
              const pct = Math.round((done / FULFILMENT_STEP_COUNT) * 100);
              return (
                <Link
                  key={o.id}
                  to={`/admin/orders?focus=${o.id}`}
                  data-testid={`my-active-order-${o.id}`}
                  className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0 hover:opacity-80"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">#{o.id.slice(-10)} — {o.customer?.name || "Guest"}</p>
                    <p className="text-xs text-welljio-subtle">
                      {o.claimed_at ? `Claimed ${new Date(o.claimed_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                    </p>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    <div className="w-28 h-1.5 bg-welljio-tint rounded-full overflow-hidden">
                      <div className="h-full bg-welljio-accent rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[10px] text-welljio-muted mt-1">{done} of {FULFILMENT_STEP_COUNT} steps</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 bg-white border border-slate-100 rounded-3xl p-7">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4" strokeWidth={1.5}/> Top products</h2>
          {stats.top_products?.length === 0 && <p className="text-sm text-welljio-muted">No paid orders yet.</p>}
          <div className="space-y-3">
            {stats.top_products?.map((p, i) => (
              <div key={p.product_id} className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-3 last:pb-0">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-welljio-tint text-xs flex items-center justify-center font-semibold">{i+1}</span>
                  <p className="font-medium">{p.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{p.units} sold</p>
                  {p.revenue !== undefined && <p className="text-xs text-welljio-muted">{inr(p.revenue)}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-slate-100 rounded-3xl p-7">
          <h2 className="font-heading text-xl font-semibold mb-4">Recent activity</h2>
          <div className="space-y-3">
            {stats.recent_orders?.map((o) => (
              <Link
                key={o.id}
                to={`/admin/orders?focus=${o.id}`}
                data-testid={`recent-order-${o.id}`}
                className="block border-b border-slate-100 last:border-0 pb-3 last:pb-0 hover:opacity-80"
              >
                <p className="text-sm font-medium truncate">{o.customer?.name || "Guest"}</p>
                <p className="text-xs text-welljio-muted capitalize">
                  {o.total !== undefined && `${inr(o.total)} · `}
                  {({placed:"pending",confirmed:"processing",out_for_delivery:"shipped"}[o.tracking_status]) || o.tracking_status || "pending"}
                </p>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
